import { Button, Checkbox, Color, Flex, Icon, Select, SubgridTable, Text } from "src/elements";
import { useLoaders } from "src/components/InlineLoader";
import {
  PnlDecision,
  RoutePostMerchflow_merchflowId_FlowUpdate,
  SwapFacingsMode,
} from "@CommonApi/merchflow/_merchflowId_/flow-update";
import { MerchflowReviewUpdateStep, ProductUpdateSwap, ProductUpdates } from "../store/types";
import { productToReadableFormat } from "../store/utils";
import { Dispatch, SetStateAction, useCallback, useMemo, useRef, useState } from "react";
import { useToasts } from "src/components/Toasts";
import { ResponseGetMerchflow_merchflowId_FlowUpdateProducts } from "@CommonApi/merchflow/_merchflowId_/flow-update/products";
import { Tooltip } from "../../../components";
import { useApi } from "src/api";
import { useTranslation } from "src/i18n";
import {
  downloadExampleXlsx,
  uploadProductsUpdatesFile,
  type UploadSummary,
} from "../store/xlsxUtils";

const PNL_DECISION_OPTIONS: { label: string; value: PnlDecision }[] = [
  { label: "Default", value: "DEFAULT" },
  { label: "Movement", value: "MOVEMENT" },
  { label: "Sales", value: "SALES" },
  { label: "Profit", value: "PROFIT" },
];

interface Props {
  merchflowId: number;
  products: ResponseGetMerchflow_merchflowId_FlowUpdateProducts;
  productUpdates: ProductUpdates;
  setProductUpdates: Dispatch<SetStateAction<ProductUpdates>>;
  setStep: (step: MerchflowReviewUpdateStep) => void;
}

export const Step3Overview = ({
  merchflowId,
  products,
  productUpdates,
  setProductUpdates,
  setStep,
}: Props) => {
  const { hasLoader } = useLoaders();
  const { toast } = useToasts();
  const { postMerchflowStoresValidateApi } = useApi();
  const { t } = useTranslation("common");

  const swapFacingsModeOptions = useMemo(
    (): { label: string; value: SwapFacingsMode }[] => [
      { label: t("swapFacingsModeFree"), value: "DOS_MOS_BALANCE" },
      { label: t("swapFacingsModeMatch"), value: "MATCH" },
    ],
    [t],
  );

  const refInputFile = useRef<HTMLInputElement>(null);

  const [uploadSummary, setUploadSummary] = useState<UploadSummary | null>(null);

  const xlsxFormatHelpTooltip = useMemo(
    () => (
      <Tooltip width="600px" textAlign="left" whiteSpace="wrap">
        <Flex column gap="12px">
          <Flex column gap="4px">
            <Text variant="small1" color={Color.white}>
              <strong>Required Columns by Sheet</strong>
            </Text>
            <Flex column gap="2px">
              {/* SWAP PRODUCTS Row */}
              <Flex gap="8px">
                <Flex width="140px">
                  <Text variant="small2" color={Color.white}>
                    SWAP PRODUCTS
                  </Text>
                </Flex>
                <Flex column gap="2px" flexGrow={1}>
                  <Text variant="small2" color={Color.white}>
                    remove_product_code, add_product_code, pnl_decision
                  </Text>
                  <Text variant="small2" color={Color.white}>
                    [swap_facings_mode, stores_codes]
                  </Text>
                </Flex>
              </Flex>
              {/* Divider */}
              <Flex height="1px" background="rgba(255, 255, 255, 0.3)" margin="4px 0" />
              {/* ADD PRODUCTS Row */}
              <Flex gap="8px">
                <Flex width="140px">
                  <Text variant="small2" color={Color.white}>
                    ADD PRODUCTS
                  </Text>
                </Flex>
                <Flex column gap="2px" flexGrow={1}>
                  <Text variant="small2" color={Color.white}>
                    add_product_code, fit_strategy, pnl_decision
                  </Text>
                  <Text variant="small2" color={Color.white}>
                    [place_to, adjacent_product_code, facings, reducible_product_code, stores_codes]
                  </Text>
                </Flex>
              </Flex>
              {/* Divider */}
              <Flex height="1px" background="rgba(255, 255, 255, 0.3)" margin="4px 0" />
              {/* REMOVE PRODUCTS Row */}
              <Flex gap="8px">
                <Flex width="140px">
                  <Text variant="small2" color={Color.white}>
                    REMOVE PRODUCTS
                  </Text>
                </Flex>
                <Flex column gap="2px" flexGrow={1}>
                  <Text variant="small2" color={Color.white}>
                    remove_product_code, pnl_decision
                  </Text>
                  <Text variant="small2" color={Color.white}>
                    [stores_codes]
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
          {/* Enum Values Section */}
          <Flex column gap="4px" margin="8px 0 0 0">
            <Text variant="small1" color={Color.white}>
              <strong>Enum Values</strong>
            </Text>
            <Flex column gap="4px">
              <Flex gap="8px">
                <Flex width="120px">
                  <Text variant="small2" color={Color.white}>
                    pnl_decision:
                  </Text>
                </Flex>
                <Flex flexGrow={1}>
                  <Text variant="small2" color={Color.white}>
                    DEFAULT | MOVEMENT | SALES | PROFIT
                  </Text>
                </Flex>
              </Flex>
              <Flex gap="8px">
                <Flex width="120px">
                  <Text variant="small2" color={Color.white}>
                    fit_strategy:
                  </Text>
                </Flex>
                <Flex flexGrow={1}>
                  <Text variant="small2" color={Color.white}>
                    KEEP_ALL_FACINGS | CAN_REDUCE_FACINGS | CAN_DELETE_RANGED_PRODUCTS |
                    CAN_DELETE_RANGED_PRODUCTS_INCLUDING_CORE_RANGE
                  </Text>
                </Flex>
              </Flex>
              <Flex gap="8px">
                <Flex width="120px">
                  <Text variant="small2" color={Color.white}>
                    swap_facings_mode:
                  </Text>
                </Flex>
                <Flex flexGrow={1}>
                  <Text variant="small2" color={Color.white}>
                    MATCH | DOS_MOS_BALANCE
                  </Text>
                </Flex>
              </Flex>
              <Flex gap="8px">
                <Flex width="120px">
                  <Text variant="small2" color={Color.white}>
                    place_to:
                  </Text>
                </Flex>
                <Flex flexGrow={1}>
                  <Text variant="small2" color={Color.white}>
                    LEFT | RIGHT
                  </Text>
                </Flex>
              </Flex>
              <Flex gap="8px">
                <Flex width="120px">
                  <Text variant="small2" color={Color.white}>
                    facings:
                  </Text>
                </Flex>
                <Flex flexGrow={1}>
                  <Text variant="small2" color={Color.white}>
                    "AUTO" | {"<"}positive integer{">"}
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </Tooltip>
    ),
    [],
  );

  const handleFileInputClick = useCallback(() => {
    refInputFile.current?.click();
  }, []);

  const handleDownloadExampleXlsx = useCallback(() => {
    downloadExampleXlsx(products);
  }, [products]);

  const handleUploadProductsUpdatesFile = useCallback(
    async (file: File) => {
      const result = await uploadProductsUpdatesFile({
        file,
        merchflowId,
        products,
        validateStores: postMerchflowStoresValidateApi,
        onError: toast,
      });

      if (result) {
        setUploadSummary(result.summary);
        setProductUpdates(result.productsUpdates);
      }
    },
    [merchflowId, postMerchflowStoresValidateApi, products, setProductUpdates, toast],
  );

  const uploadDropdownOptions = useMemo(
    () => [
      {
        label: "Upload From XLSX File",
        onClick: handleFileInputClick,
        tooltip: xlsxFormatHelpTooltip,
      },
      {
        label: "Download Template",
        onClick: handleDownloadExampleXlsx,
      },
    ],
    [handleFileInputClick, handleDownloadExampleXlsx, xlsxFormatHelpTooltip],
  );

  const next = useCallback(() => {
    setStep("STEP_4_PLANOGRAM_RULES");
  }, [setStep]);

  const updateProductProperty = useCallback(
    <T extends ProductUpdates[number], K extends keyof Omit<T, "type">>(
      index: number,
      key: K,
      value: T[K],
    ) => {
      setProductUpdates((productUpdates) =>
        productUpdates.map((productUpdate, i) =>
          i === index ? { ...productUpdate, [key]: value } : productUpdate,
        ),
      );
    },
    [setProductUpdates],
  );

  return (
    <>
      {uploadSummary && (
        <Flex
          justify="center"
          align="center"
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          background="rgba(0, 0, 0, 0.4)"
          zIndex={1000}
        >
          <Flex
            column
            gap="16px"
            background="white"
            padding="24px"
            borderRadius="8px"
            maxWidth="800px"
            width="90%"
            maxHeight="80vh"
            overflow="hidden"
          >
            <Text variant="h4">Update Summary</Text>

            <Flex gap="24px">
              <Text variant="body2">Swap products: {uploadSummary.swapCount}</Text>
              <Text variant="body2">Add products: {uploadSummary.addCount}</Text>
              <Text variant="body2">Remove products: {uploadSummary.removeCount}</Text>
            </Flex>

            <Flex gap="8px" align="center">
              <Icon name="warningCircle" color={Color.red} size={18} />
              <Text variant="h5">Errors</Text>
            </Flex>
            {uploadSummary.errors.length === 0 ? (
              <Text variant="small2">No validation errors.</Text>
            ) : (
              <SubgridTable
                align="center"
                columns={[
                  { id: "operator", header: "Operator", width: "100px" },
                  { id: "rowNumber", header: "Row", width: "80px" },
                  { id: "message", header: "Error", width: "3fr" },
                ]}
                data={uploadSummary.errors.map((error, index) => ({
                  id: index,
                  operator: error.operator,
                  rowNumber: error.rowNumber,
                  message: error.message,
                }))}
              />
            )}

            <Flex justify="right">
              <Button onClick={() => setUploadSummary(null)}>OK</Button>
            </Flex>
          </Flex>
        </Flex>
      )}

      <Flex width="100%" column>
        <Flex gap="20px" margin="20px 0" flexGrow>
          <Flex column gap="10px">
            <Button color="greenSmoke" size="big" onClick={() => setStep("STEP_3A_SWAP_PRODUCTS")}>
              Swap Products
            </Button>

            <Button
              color="greenSmoke"
              size="big"
              onClick={() => setStep("STEP_3B_REMOVE_PRODUCTS")}
            >
              Remove Products
            </Button>

            <Button color="greenSmoke" size="big" onClick={() => setStep("STEP_3C_ADD_PRODUCTS")}>
              Add Products
            </Button>

            <>
              <Button
                color="greenSmoke"
                variant="inverted"
                size="big"
                dropdown={uploadDropdownOptions}
              >
                Upload
              </Button>

              <input
                ref={refInputFile}
                type="file"
                accept=".xlsx"
                onChange={(event) => {
                  if (event.target.files?.length === 1) {
                    handleUploadProductsUpdatesFile(event.target.files[0]);
                  }
                  event.target.value = "";
                }}
                style={{ display: "none" }}
              />
            </>
          </Flex>

          <SubgridTable
            align="center"
            columns={[
              {
                id: "index",
                header: "Product Change",
                width: "120px",
              },
              {
                id: "type",
                header: "Operator",
                width: "120px",
                renderer: (data) =>
                  data.row.type.slice(0, 1).toUpperCase() + data.row.type.slice(1),
              },
              {
                id: "details",
                header: "Details",
                width: "3fr",
                renderer: (data) =>
                  (data.row.type === "remove" && productToReadableFormat(data.row.product)) ||
                  (data.row.type === "swap" && (
                    <Flex direction="column" gap="5px">
                      <Text variant="small2">OUT: {productToReadableFormat(data.row.remove)}</Text>

                      <Text variant="small2">
                        IN: {data.row.add.map(productToReadableFormat).join(", ")}
                      </Text>
                    </Flex>
                  )) ||
                  (data.row.type === "add" && productToReadableFormat(data.row.productToAdd)) ||
                  "",
              },
              {
                id: "fitStrategy",
                header: "Fit Strategy",
                width: "140px",
                renderer: (data) =>
                  (data.row.type === "add" &&
                    data.row.fitStrategy === "KEEP_ALL_FACINGS" &&
                    "No other products to be reduced") ||
                  (data.row.type === "add" &&
                    data.row.fitStrategy === "CAN_REDUCE_FACINGS" &&
                    "Any product could be reduced") ||
                  (data.row.type === "add" &&
                    data.row.fitStrategy === "CAN_DELETE_RANGED_PRODUCTS" &&
                    "Any non-core product could be reduced / deleted") ||
                  (data.row.type === "add" &&
                    data.row.fitStrategy === "CAN_DELETE_RANGED_PRODUCTS_INCLUDING_CORE_RANGE" &&
                    "Any product could be reduced / deleted") ||
                  "-",
              },
              {
                id: "pnlDecision",
                header: "PNL Decision",
                width: "140px",
                renderer: ({ row, index }) => (
                  <Select
                    options={PNL_DECISION_OPTIONS}
                    isDisabled={row.type !== "swap" && row.type !== "add"}
                    value={row.pnlDecision}
                    setValue={(value) => updateProductProperty(index, "pnlDecision", value)}
                  />
                ),
              },
              {
                id: "swapFacingsMode",
                header: t("swapFacingsModeLabel"),
                width: "140px",
                renderer: ({ row, index }) =>
                  row.type === "swap" ? (
                    <Select
                      options={swapFacingsModeOptions}
                      value={row.swapFacingsMode}
                      setValue={(value) =>
                        updateProductProperty<ProductUpdateSwap, "swapFacingsMode">(
                          index,
                          "swapFacingsMode",
                          value,
                        )
                      }
                    />
                  ) : (
                    "-"
                  ),
              },
              {
                id: "canSqueezeSwapTo",
                header: "Squeeze To",
                width: "100px",
                renderer: ({ row, index }) => (
                  <Checkbox
                    isChecked={row.type === "swap" ? row.canSqueezeSwapTo : false}
                    isDisabled={row.type !== "swap"}
                    setIsChecked={(value) =>
                      updateProductProperty<ProductUpdateSwap, "canSqueezeSwapTo">(
                        index,
                        "canSqueezeSwapTo",
                        value,
                      )
                    }
                  />
                ),
              },
              {
                id: "stores",
                header: "stores",
                width: "140px",
                renderer: ({ row }) =>
                  row.storeCodes.length === 0 ? "ALL" : row.storeCodes.join(", "),
              },
            ]}
            data={productUpdates}
          />
        </Flex>

        <Flex justify="right">
          <Button isLoading={hasLoader(RoutePostMerchflow_merchflowId_FlowUpdate)} onClick={next}>
            Next
          </Button>
        </Flex>
      </Flex>
    </>
  );
};
